import React, { useEffect, useRef } from "react";

/**
 * Playful, kid-friendly shell for the auth screens (login, password reset,
 * account setup). Mirrors the in-app "game" language: indigo/blue gradient
 * background with soft blobs, a white rounded card with a branded header
 * band and an icon bubble. Keeps the flow visually consistent with what
 * kids see once inside the app.
 *
 * Keyboard behaviour, which is most of what is going on below.
 *
 * In the phone app the soft keyboard takes roughly half the screen, and
 * Capacitor is configured with `resize: "body"` — so the page's usable height
 * shrinks the moment a field is focused. This shell used to be
 * `min-h-screen overflow-hidden`, which is two problems at once: `100vh` does
 * not shrink with the keyboard, and `overflow-hidden` meant that whatever fell
 * below the fold could not be scrolled to. With the keyboard open on the login
 * screen that was the "remember me" box, the sign-in button and the
 * forgotten-password link: visible in the designer's browser, unreachable on a
 * phone.
 *
 * So: a dynamic viewport unit, vertical scrolling allowed, and the card
 * centred with auto margins rather than `justify-center` — auto margins centre
 * when there is room and still let you scroll to BOTH ends when there is not,
 * which `justify-center` on an overflowing flex container famously does not.
 */
export default function AuthShell({
  children,
  icon,
  title,
  subtitle,
  topRight,
  footer,
  maxWidthClass = "max-w-md",
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  topRight?: React.ReactNode;
  footer?: React.ReactNode;
  maxWidthClass?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /**
     * Clear the in-app scroll lock, in case it is still on.
     *
     * MobileAppLayout pins html/body to the viewport with `overflow: hidden`
     * while it is mounted and removes the class when it unmounts. An auth page
     * is never inside that layout, so if the class is present here something
     * went wrong on the way out — and the symptom is brutal: the page is
     * clipped at the pinned height with no way to scroll, the rest of the
     * screen showing the dark body colour behind it. Cheap to rule out, so we
     * rule it out rather than trusting the cleanup.
     */
    document.documentElement.classList.remove("mobile-app-scroll-lock");
  }, []);

  useEffect(() => {
    /**
     * Bring the focused field above the keyboard.
     *
     * Android raises the keyboard over the lower half of the screen; without
     * this, tapping the password box can leave it exactly underneath. The
     * delay lets the keyboard animation and the resize settle first, the same
     * way MobileAppLayout does it for the app's own forms.
     */
    const el = scrollRef.current;
    if (!el) return;
    let timer: number | undefined;
    const onFocusIn = (e: Event) => {
      const target = e.target as HTMLElement | null;
      if (!target?.tagName) return;
      if (!["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        target.scrollIntoView({ block: "center", behavior: "smooth" });
      }, 350);
    };
    el.addEventListener("focusin", onFocusIn);
    return () => {
      window.clearTimeout(timer);
      el.removeEventListener("focusin", onFocusIn);
    };
  }, []);

  return (
    <div
      ref={scrollRef}
      // `min-h-[100dvh]` after `min-h-screen`: the second wins where dynamic
      // viewport units are supported and the first is the fallback where they
      // are not. overflow-x is clipped (the background blobs stick out on
      // purpose); overflow-y must scroll, see the note above.
      className="relative flex flex-col min-h-screen min-h-[100dvh] overflow-x-hidden overflow-y-auto bg-gradient-to-br from-indigo-500 via-indigo-600 to-blue-700"
    >
      {/* Playful background blobs */}
      <div className="pointer-events-none absolute -top-24 -left-16 w-72 h-72 rounded-full bg-white/10 blur-2xl" />
      <div className="pointer-events-none absolute top-1/3 -right-24 w-80 h-80 rounded-full bg-white/10 blur-2xl" />
      <div className="pointer-events-none absolute -bottom-24 left-1/4 w-72 h-72 rounded-full bg-sky-300/10 blur-2xl" />

      {topRight && <div className="absolute top-4 right-4 z-20">{topRight}</div>}

      <div className="relative z-10 flex flex-col items-center w-full px-4 py-8 m-auto">
        <div className={`w-full ${maxWidthClass} animate-zoom`}>
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
            {/* Branded header band */}
            <div className="relative bg-gradient-to-br from-indigo-500 via-indigo-600 to-blue-600 px-6 pt-7 pb-10 text-center">
              <div className="pointer-events-none absolute -top-6 -right-4 w-24 h-24 rounded-full bg-white/10" />
              <div className="pointer-events-none absolute -bottom-8 -left-4 w-24 h-24 rounded-full bg-white/10" />
              <img src="/assets/ig_h_white.png" alt="iGROWth" className="relative h-9 w-auto mx-auto" />
            </div>

            {/* Icon bubble overlapping the band */}
            {icon && (
              <div className="relative -mt-9 flex justify-center">
                <div className="w-16 h-16 rounded-2xl bg-white shadow-lg grid place-items-center ring-4 ring-white">
                  {icon}
                </div>
              </div>
            )}

            <div className={`px-6 pb-7 ${icon ? "pt-3" : "pt-6"}`}>
              {title && <h1 className="text-center text-xl font-extrabold text-slate-800">{title}</h1>}
              {subtitle && <p className="text-center text-sm text-slate-500 mt-1.5 leading-relaxed">{subtitle}</p>}
              <div className="mt-5">{children}</div>
            </div>
          </div>

          {footer && <div className="mt-5">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

/** Playful labelled input for the auth forms. */
export function AuthField({
  label,
  className = "",
  ...props
}: { label: React.ReactNode } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1.5">{label}</span>
      <input
        className={`w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition ${className}`}
        {...props}
      />
    </label>
  );
}

/** Google Play + App Store download badges. */
export function StoreBadges({ className = "" }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center gap-3 flex-wrap ${className}`}>
      <a
        href="https://play.google.com/store/apps/details?id=com.sportiveducat.app"
        target="_blank"
        rel="noopener noreferrer"
        className="transition-transform active:scale-95 hover:opacity-90"
        aria-label="Descarcă din Google Play"
      >
        <img
          src="https://play.google.com/intl/en_us/badges/static/images/badges/ro_badge_web_generic.png"
          alt="Descarcă din Google Play"
          className="h-12 w-auto"
        />
      </a>
      <a
        href="https://apps.apple.com/ro/app/igrowth/id6767212760"
        target="_blank"
        rel="noopener noreferrer"
        className="transition-transform active:scale-95 hover:opacity-90"
        aria-label="Descarcă din App Store"
      >
        <img
          src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/ro-ro?releaseDate=1717977600"
          alt="Descarcă din App Store"
          className="h-[42px] w-auto"
        />
      </a>
    </div>
  );
}

/** EU co-funding legal note (required on every public auth page). */
export function EuFundingNote({ light = false }: { light?: boolean }) {
  return (
    <p className={`text-[10px] leading-relaxed text-center ${light ? "text-white/70" : "text-slate-500"}`}>
      Această platformă web și aplicație mobilă au fost realizate cu sprijinul financiar al Uniunii Europene prin
      Programul Educației și Ocupare 2021-2027, cofinanțat din FSE+. Conținutul acestui material nu reprezintă în mod
      obligatoriu poziția oficială a Uniunii Europene sau a Guvernului României. Pentru informații detaliate despre
      celelalte programe finanțate de Uniunea Europeană, vizitați{" "}
      <a href="https://www.fonduri-ue.ro" target="_blank" rel="nofollow noreferrer" className="underline">
        www.fonduri-ue.ro
      </a>
      .
    </p>
  );
}
